import { create } from 'zustand';
import { api } from '@/lib/api';
import { executeRelayWrite } from '@/lib/relay-client';
import { useAuthStore } from '@/stores/auth';
import type { SeedTableDef, SeedColumnDef } from '@/types/seed';
import { formatValue } from '@/lib/format-value';

export interface SeedPipeline {
  id: string;
  name: string;
  seedTables: SeedTableDef[];
}

interface SeedDataState {
  pipelines: SeedPipeline[];
  selectedPipelineId: string | null;
  selectedTableName: string | null;
  tableData: { columns: string[]; rows: Record<string, unknown>[] } | null;
  dirtyRows: Map<string, Record<string, unknown>>;
  newRowPks: Set<string>;
  lookupOptions: Map<string, string[]>;
  loading: boolean;
  saving: boolean;
  error: string | null;

  loadPipelines: () => Promise<void>;
  selectPipeline: (id: string) => void;
  selectTable: (name: string) => Promise<void>;
  loadTableData: (tableDef: SeedTableDef) => Promise<void>;
  loadLookupOptions: (column: string, lookupDef: NonNullable<SeedColumnDef['lookup']>) => Promise<void>;
  updateCell: (rowPk: string, column: string, value: unknown) => void;
  addRow: () => void;
  deleteRow: (rowPk: string) => void;
  saveChanges: () => Promise<void>;
  downloadCsv: () => void;
  uploadCsv: (file: File) => Promise<void>;
  reset: () => void;
}

function getPrimaryKeyValue(row: Record<string, unknown>, primaryKey: string[]): string {
  const computed = primaryKey.map((k) => formatValue(row[k])).join('::');
  // Fall back to hidden tracking ID for newly-added rows with empty PK columns
  return computed || (row.__newRowId as string) || '';
}

export const useSeedDataStore = create<SeedDataState>()((set, get) => ({
  pipelines: [],
  selectedPipelineId: null,
  selectedTableName: null,
  tableData: null,
  dirtyRows: new Map(),
  newRowPks: new Set(),
  lookupOptions: new Map(),
  loading: false,
  saving: false,
  error: null,

  loadPipelines: async () => {
    set({ loading: true, error: null });
    try {
      const user = useAuthStore.getState().user;
      const { data, error } = await api.api.v1['visual-pipelines'].get({
        query: { environment_id: user?.active_environment_id ?? '' },
      });
      if (error || !data) {
        set({ loading: false, error: 'Failed to load pipelines' });
        return;
      }

      // Filter pipelines that have seed tables in pipeline_json
      const seedPipelines: SeedPipeline[] = [];
      for (const p of data as { id: string; name: string; pipeline_json?: Record<string, unknown> }[]) {
        const pJson = p.pipeline_json;
        if (!pJson) {
          continue;
        }

        // Collect seed definitions from v1 (top-level seed[]) or v2 (steps[] with type=seed)
        let seedDefs: Record<string, unknown>[];
        if (Array.isArray(pJson.seed)) {
          seedDefs = pJson.seed as Record<string, unknown>[];
        } else if (pJson.version === 2 && Array.isArray(pJson.steps)) {
          seedDefs = (pJson.steps as Record<string, unknown>[]).filter((s) => s.type === 'seed');
        } else {
          continue;
        }

        if (seedDefs.length === 0) {
          continue;
        }

        const seedTables: SeedTableDef[] = seedDefs.map((s) => ({
          name: (s.name as string) || '',
          output: (s.output as string) || '',
          mode: (s.mode as 'merge' | 'replace') || 'merge',
          primary_key: (s.primary_key as string[]) || (s.keys as string[]) || [],
          columns: Array.isArray(s.columns)
            ? (s.columns as Record<string, unknown>[]).map((c) => ({
                name: (c.name as string) || '',
                label: c.label as string | undefined,
                type: c.type as string | undefined,
                seeded: c.seeded as boolean | undefined,
                editable: c.editable as boolean | undefined,
                lookup: c.lookup as SeedColumnDef['lookup'],
                description: c.description as string | undefined,
              }))
            : [],
          dsl: s.dsl as string | undefined,
          default_rows: s.default_rows as Record<string, unknown>[] | undefined,
          description: s.description as string | undefined,
        }));

        if (seedTables.length > 0) {
          seedPipelines.push({ id: p.id, name: p.name, seedTables });
        }
      }

      set({ pipelines: seedPipelines, loading: false });
    } catch {
      set({ loading: false, error: 'Failed to load pipelines' });
    }
  },

  selectPipeline: (id) => {
    const pipeline = get().pipelines.find((p) => p.id === id);
    set({
      selectedPipelineId: id,
      selectedTableName: pipeline?.seedTables[0]?.name ?? null,
      tableData: null,
      dirtyRows: new Map(),
      newRowPks: new Set(),
      lookupOptions: new Map(),
      error: null,
    });

    // Auto-load first table
    if (pipeline?.seedTables[0]) {
      void get().loadTableData(pipeline.seedTables[0]);
    }
  },

  selectTable: async (name) => {
    const pipeline = get().pipelines.find((p) => p.id === get().selectedPipelineId);
    const tableDef = pipeline?.seedTables.find((t) => t.name === name);
    set({
      selectedTableName: name,
      tableData: null,
      dirtyRows: new Map(),
      newRowPks: new Set(),
      lookupOptions: new Map(),
      error: null,
    });
    if (tableDef) {
      await get().loadTableData(tableDef);
    }
  },

  loadTableData: async (tableDef) => {
    set({ loading: true, error: null });
    try {
      const dsl = `source("${tableDef.output}").limit(1000)`;
      const { data, error } = await api.api.v1.transforms.preview.post({ dsl, limit: 1000 });

      if (error || !data || !('columns' in data)) {
        // Table might not exist yet — show empty state with column defs
        if (tableDef.columns && tableDef.columns.length > 0) {
          set({
            tableData: {
              columns: tableDef.columns.map((c) => c.name),
              rows: [],
            },
            loading: false,
          });
        } else {
          set({ loading: false, error: 'Failed to load table data' });
        }
        return;
      }

      const raw = data as { columns: { name: string; type: string }[]; rows: Record<string, unknown>[] };
      set({ tableData: { columns: raw.columns.map((c) => c.name), rows: raw.rows }, loading: false });

      // Load lookup options for columns that have lookup definitions
      if (tableDef.columns) {
        for (const col of tableDef.columns) {
          if (col.lookup) {
            void get().loadLookupOptions(col.name, col.lookup);
          }
        }
      }
    } catch {
      set({ loading: false, error: 'Failed to load table data' });
    }
  },

  loadLookupOptions: async (column, lookupDef) => {
    try {
      let dsl: string;
      if (lookupDef.dsl) {
        dsl = lookupDef.dsl;
      } else if (lookupDef.seed && lookupDef.column) {
        // Look up from another seed table
        const pipeline = get().pipelines.find((p) => p.id === get().selectedPipelineId);
        const lookupTable = pipeline?.seedTables.find((t) => t.name === lookupDef.seed);
        if (!lookupTable) {
          return;
        }
        dsl = `source("${lookupTable.output}").select([${lookupDef.column}]).dedupe([${lookupDef.column}]).limit(500)`;
      } else {
        return;
      }

      const { data, error } = await api.api.v1.transforms.preview.post({ dsl, limit: 500 });
      if (error || !data || !('rows' in data)) {
        return;
      }

      const result = data as { columns: { name: string; type: string }[]; rows: Record<string, unknown>[] };
      const col = lookupDef.column || result.columns[0]?.name;
      const options = result.rows.map((r) => formatValue(r[col])).filter((v) => v !== '');

      set((s) => {
        const next = new Map(s.lookupOptions);
        next.set(column, options);
        return { lookupOptions: next };
      });
    } catch {
      // silently fail
    }
  },

  updateCell: (rowPk, column, value) => {
    set((s) => {
      const pipeline = s.pipelines.find((p) => p.id === s.selectedPipelineId);
      const tableDef = pipeline?.seedTables.find((t) => t.name === s.selectedTableName);
      const pk = tableDef?.primary_key || [];

      const next = new Map(s.dirtyRows);
      const existing = next.get(rowPk) || {};
      const updatedDirty = { ...existing, [column]: value };

      // If a PK column was edited on a new row, re-key the tracking maps
      const isPkColumn = pk.includes(column);
      const isNewRow = s.newRowPks.has(rowPk);
      let nextNewPks = s.newRowPks;

      if (s.tableData) {
        const updatedRows = s.tableData.rows.map((row) => {
          if (getPrimaryKeyValue(row, pk) === rowPk) {
            return { ...row, [column]: value };
          }
          return row;
        });

        if (isPkColumn && isNewRow) {
          // Compute new PK after the cell update
          const updatedRow = updatedRows.find((r) => {
            // Find the row we just updated — check by matching all other PK cols + new value
            const rowCopy = { ...r };
            return getPrimaryKeyValue(rowCopy, pk) !== rowPk || rowCopy[column] === value;
          });
          const newPkValue = updatedRow ? getPrimaryKeyValue(updatedRow, pk) : rowPk;

          if (newPkValue !== rowPk) {
            // Re-key dirty rows
            next.delete(rowPk);
            next.set(newPkValue, updatedDirty);
            // Re-key new row tracking
            nextNewPks = new Set(s.newRowPks);
            nextNewPks.delete(rowPk);
            nextNewPks.add(newPkValue);
          } else {
            next.set(rowPk, updatedDirty);
          }
        } else {
          next.set(rowPk, updatedDirty);
        }

        return { dirtyRows: next, newRowPks: nextNewPks, tableData: { ...s.tableData, rows: updatedRows } };
      }

      next.set(rowPk, updatedDirty);
      return { dirtyRows: next, newRowPks: nextNewPks };
    });
  },

  addRow: () => {
    set((s) => {
      if (!s.tableData) {
        return s;
      }
      const pipeline = s.pipelines.find((p) => p.id === s.selectedPipelineId);
      const tableDef = pipeline?.seedTables.find((t) => t.name === s.selectedTableName);
      if (!tableDef) {
        return s;
      }

      const newRow: Record<string, unknown> = {};
      for (const col of s.tableData.columns) {
        newRow[col] = '';
      }

      // Hidden tracking ID — not a visible column, used for PK matching on new rows
      const pk = tableDef.primary_key || [];
      newRow.__newRowId = `__new_${Date.now()}`;
      const rowPkValue = getPrimaryKeyValue(newRow, pk);

      const next = new Map(s.dirtyRows);
      next.set(rowPkValue, newRow);

      const nextNewPks = new Set(s.newRowPks);
      nextNewPks.add(rowPkValue);

      return {
        tableData: {
          ...s.tableData,
          rows: [...s.tableData.rows, newRow],
        },
        dirtyRows: next,
        newRowPks: nextNewPks,
      };
    });
  },

  deleteRow: (rowPk) => {
    set((s) => {
      if (!s.tableData) {
        return s;
      }
      const pipeline = s.pipelines.find((p) => p.id === s.selectedPipelineId);
      const tableDef = pipeline?.seedTables.find((t) => t.name === s.selectedTableName);
      const pk = tableDef?.primary_key || [];

      const updatedRows = s.tableData.rows.filter((row) => getPrimaryKeyValue(row, pk) !== rowPk);
      const next = new Map(s.dirtyRows);
      next.delete(rowPk);

      return {
        tableData: { ...s.tableData, rows: updatedRows },
        dirtyRows: next,
      };
    });
  },

  saveChanges: async () => {
    const s = get();
    if (s.dirtyRows.size === 0) {
      return;
    }

    const pipeline = s.pipelines.find((p) => p.id === s.selectedPipelineId);
    const tableDef = pipeline?.seedTables.find((t) => t.name === s.selectedTableName);
    if (!tableDef || !pipeline) {
      return;
    }

    set({ saving: true, error: null });

    try {
      const pk = tableDef.primary_key || [];
      const now = new Date().toISOString();

      // Only send rows that were actually changed or added — not the full table.
      // Uses mode: merge_replace which reads existing + merges new + dedupes on keys + writes clean.
      const changedRows = (s.tableData?.rows || [])
        .filter((row) => {
          const rowPk = getPrimaryKeyValue(row, pk);
          return s.dirtyRows.has(rowPk) || s.newRowPks.has(rowPk);
        })
        .map((row) => {
          const clean = { ...row };
          delete clean.__newRowId;
          delete clean._extracted_at;
          delete clean._row_hash;
          clean._updated_at = now;
          if (s.newRowPks.has(getPrimaryKeyValue(row, pk)) && !clean._created_at) {
            clean._created_at = now;
          }
          return clean;
        });

      // Coerce all values to strings so mock() JSON matches the table schema.
      // The relay infers column types from JSON — mixing numbers and strings in the
      // same column (e.g. edited "120001" vs original 1200) causes a type mismatch.
      const stringifiedRows = changedRows.map((row) => {
        const out: Record<string, unknown> = {};
        for (const [k, v] of Object.entries(row)) {
          out[k] = v == null ? null : formatValue(v);
        }
        return out;
      });
      const jsonData = JSON.stringify(stringifiedRows);
      const pkList = pk.join(', ');
      const dsl = `mock(${jsonData}).output("${tableDef.output}", mode: merge_replace, keys: [${pkList}])`;

      // Use relay /execute endpoint — handles mock() + .output() for catalog writes
      const result = await executeRelayWrite(dsl);

      if (result.error) {
        set({ saving: false, error: `Save failed: ${result.error}` });
        return;
      }

      set({ dirtyRows: new Map(), newRowPks: new Set(), saving: false });
    } catch {
      set({ saving: false, error: 'Failed to save changes' });
    }
  },

  downloadCsv: () => {
    const s = get();
    if (!s.tableData) {
      return;
    }

    const pipeline = s.pipelines.find((p) => p.id === s.selectedPipelineId);
    const tableDef = pipeline?.seedTables.find((t) => t.name === s.selectedTableName);
    if (!tableDef) {
      return;
    }

    // Use column defs for headers (strict order), fall back to tableData.columns
    const columns = tableDef.columns?.map((c) => c.name) ?? s.tableData.columns;
    // Filter out system columns
    const exportCols = columns.filter((c) => c !== '_extracted_at' && c !== '_row_hash' && c !== '__newRowId');

    const escapeCsvField = (val: unknown): string => {
      const str = formatValue(val);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const headerLine = exportCols.map(escapeCsvField).join(',');
    const dataLines = s.tableData.rows.map((row) => exportCols.map((col) => escapeCsvField(row[col])).join(','));
    const csv = [headerLine, ...dataLines].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${s.selectedTableName ?? 'seed-data'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  },

  uploadCsv: async (file: File) => {
    const s = get();
    const pipeline = s.pipelines.find((p) => p.id === s.selectedPipelineId);
    const tableDef = pipeline?.seedTables.find((t) => t.name === s.selectedTableName);
    if (!tableDef) {
      set({ error: 'No table selected' });
      return;
    }

    set({ error: null });

    try {
      const text = await file.text();
      const lines = text.split('\n').filter((l) => l.trim() !== '');
      if (lines.length === 0) {
        set({ error: 'CSV file is empty' });
        return;
      }

      // Parse header
      const parseRow = (line: string): string[] => {
        const fields: string[] = [];
        let current = '';
        let inQuotes = false;
        for (let i = 0; i < line.length; i++) {
          const ch = line[i];
          if (inQuotes) {
            if (ch === '"' && line[i + 1] === '"') {
              current += '"';
              i++;
            } else if (ch === '"') {
              inQuotes = false;
            } else {
              current += ch;
            }
          } else {
            if (ch === '"') {
              inQuotes = true;
            } else if (ch === ',') {
              fields.push(current);
              current = '';
            } else {
              current += ch;
            }
          }
        }
        fields.push(current);
        return fields;
      };

      const headers = parseRow(lines[0]).map((h) => h.trim());

      // Validate headers match column defs exactly
      const expectedCols = (tableDef.columns?.map((c) => c.name) ?? s.tableData?.columns ?? []).filter(
        (c) => c !== '_extracted_at' && c !== '_row_hash' && c !== '__newRowId',
      );

      if (headers.length !== expectedCols.length) {
        set({
          error: `CSV has ${headers.length} columns, expected ${expectedCols.length}: ${expectedCols.join(', ')}`,
        });
        return;
      }

      for (let i = 0; i < expectedCols.length; i++) {
        if (headers[i] !== expectedCols[i]) {
          set({ error: `Column mismatch at position ${i + 1}: got "${headers[i]}", expected "${expectedCols[i]}"` });
          return;
        }
      }

      // Parse data rows
      const rows: Record<string, unknown>[] = [];
      for (let i = 1; i < lines.length; i++) {
        const values = parseRow(lines[i]);
        const row: Record<string, unknown> = {};
        for (let j = 0; j < headers.length; j++) {
          row[headers[j]] = values[j] ?? '';
        }
        rows.push(row);
      }

      // Replace table data and mark all rows as dirty
      const pk = tableDef.primary_key || [];
      const dirtyRows = new Map<string, Record<string, unknown>>();
      for (const row of rows) {
        const pkValue = getPrimaryKeyValue(row, pk);
        dirtyRows.set(pkValue, row);
      }

      set({
        tableData: { columns: expectedCols, rows },
        dirtyRows,
        newRowPks: new Set(),
      });
    } catch {
      set({ error: 'Failed to parse CSV file' });
    }
  },

  reset: () => {
    set({
      pipelines: [],
      selectedPipelineId: null,
      selectedTableName: null,
      tableData: null,
      dirtyRows: new Map(),
      newRowPks: new Set(),
      lookupOptions: new Map(),
      loading: false,
      saving: false,
      error: null,
    });
  },
}));
